import axios from 'axios';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { API_URL } from '../config/api';

/*
 * Magazyn — klient API (/api/stock, /api/settings/stock). Typy lustrzane do
 * v2/api/src/modules/stock (model.ts, service.ts, config.ts). Wspólne dla kiosku i panelu admina.
 */

export type StockMovementKind = 'delivery' | 'restock' | 'consume' | 'remove' | 'adjust';
export type OrderStockStatus = 'ok' | 'partial' | 'short';

export interface StockTagLabels {
  ok: string;
  partial: string;
  short: string;
}

export interface StockConfig {
  enabled: boolean;
  resetEnabled: boolean;
  /** HH:MM, czas polski; '00:00' = północ (pokazywane jako 24:00). */
  resetTime: string;
  groupBy: 'variant' | 'product';
  productGrouping: Record<string, 'variant' | 'product'>;
  autoTrackNew: boolean;
  demandScope: 'now' | 'all';
  includeUnidentified: boolean;
  allocation: 'whole' | 'greedy';
  showOrderTags: boolean;
  tagLabels: StockTagLabels;
  warnOnStart: boolean;
  quickSteps: number[];
  removalReasons: string[];
  roundShortageToStep: boolean;
  printCopies: number;
}

export interface StockItemView {
  id: string;
  key: string;
  /** Nazwa do wyświetlenia: nazwa własna albo „Tytuł (Wariant)". */
  name: string;
  title: string;
  variantTitle: string | null;
  displayName: string | null;
  sku: string | null;
  barcode: string | null;
  /** Nazwa ikony lucide (kebab-case). */
  icon: string;
  step: number;
  safetyStock: number;
  carryOver: boolean;
  sortOrder: number | null;
  /** Może być ujemny: spakowano więcej, niż wpisano dostaw. */
  onHand: number;
  demand: number;
  ordersCount: number;
  allocated: number;
  shortage: number;
  toTake: number;
  free: number;
  low: boolean;
  /** Ma potrzebę, stan albo ruch od resetu — pokazuj domyślnie; resztę pod „Pokaż wszystkie". */
  active: boolean;
  today: { delivered: number; restocked: number; consumed: number; removed: number; adjusted: number };
  lastMovementAt: string | null;
}

export interface OrderStockTag {
  orderId: string;
  orderName: string;
  status: OrderStockStatus;
  label: string;
  units: number;
  missing: Array<{ key: string; name: string; quantity: number }>;
}

export interface StockRunLine {
  key: string;
  name: string;
  variantTitle: string | null;
  sku: string | null;
  quantity: number;
  demand: number;
  onHand: number;
}

export interface StockRun {
  id: string;
  status: 'open' | 'settled' | 'cancelled';
  createdAt: string;
  createdByName: string | null;
  lines: StockRunLine[];
  totalUnits: number;
  settledAt: string | null;
  settledLines: Array<{ key: string; quantity: number }> | null;
}

export interface StockTotals {
  onHand: number;
  demand: number;
  shortage: number;
  toTake: number;
  free: number;
  delivered: number;
  restocked: number;
  consumed: number;
  removed: number;
  ordersTotal: number;
  ordersOk: number;
  ordersPartial: number;
  ordersShort: number;
}

export interface StockOverview {
  enabled: boolean;
  generatedAt: string;
  resetEnabled: boolean;
  resetTime: string;
  windowStart: string | null;
  nextResetAt: string | null;
  /** Od resetu coś wpisano — bez tego znaczniki przy zamówieniach są ukryte. */
  stockKnown: boolean;
  demandScope: 'now' | 'all';
  /** false = UCS niedostępny: stan widać, zapotrzebowanie nie. */
  demandAvailable: boolean;
  demandError: string | null;
  items: StockItemView[];
  totals: StockTotals;
  orders: OrderStockTag[];
  openRun: StockRun | null;
  config: Pick<
    StockConfig,
    'quickSteps' | 'removalReasons' | 'tagLabels' | 'showOrderTags' | 'warnOnStart' | 'roundShortageToStep' | 'allocation' | 'groupBy'
  >;
}

export interface StockCatalogItem {
  id: string;
  key: string;
  productId: string | null;
  variantId: string | null;
  sku: string | null;
  barcode: string | null;
  title: string;
  variantTitle: string | null;
  displayName: string | null;
  icon: string | null;
  tracked: boolean;
  carryOver: boolean;
  step: number;
  safetyStock: number;
  sortOrder: number | null;
  createdAt: string | null;
}

export interface StockHistoryEntry {
  batchId: string;
  kind: StockMovementKind;
  createdAt: string;
  actorName: string | null;
  actorKind: string | null;
  stationId: string | null;
  reason: string | null;
  orderName: string | null;
  runId: string | null;
  isUndo: boolean;
  refBatchId: string | null;
  undone: boolean;
  canUndo: boolean;
  totalDelta: number;
  lines: Array<{ key: string; name: string; delta: number }>;
}

export type StockLineInput = { key: string; quantity: number };

/* ---------- Zapytania ---------- */

export const STOCK_KEYS = {
  overview: ['stock-overview'] as const,
  config: ['stock-config'] as const,
  items: ['stock-items'] as const,
  history: ['stock-history'] as const,
};

/** Przegląd magazynu (stan + zapotrzebowanie + znaczniki). Odświeżany co 15 s jak kolejka. */
export function useStockOverview(opts: { enabled?: boolean; refetchInterval?: number } = {}) {
  return useQuery<StockOverview>({
    queryKey: STOCK_KEYS.overview,
    queryFn: async () => (await axios.get(`${API_URL}/api/stock/overview`)).data,
    refetchInterval: opts.refetchInterval ?? 15000,
    enabled: opts.enabled ?? true,
    retry: 1,
  });
}

/** Ustawienia modułu — czy włączony, godzina resetu itd. Czytane też przez kiosk. */
export function useStockConfig(opts: { enabled?: boolean } = {}) {
  return useQuery<StockConfig>({
    queryKey: STOCK_KEYS.config,
    queryFn: async () => (await axios.get(`${API_URL}/api/settings/stock`)).data,
    staleTime: 60_000,
    refetchInterval: 5 * 60_000,
    enabled: opts.enabled ?? true,
    // Starsze API bez Magazynu → 404: traktujemy jak wyłączony moduł, bez ponawiania.
    retry: false,
  });
}

export function useStockItems(opts: { enabled?: boolean } = {}) {
  return useQuery<{ items: StockCatalogItem[]; icons: string[] }>({
    queryKey: STOCK_KEYS.items,
    queryFn: async () => (await axios.get(`${API_URL}/api/stock/items`)).data,
    enabled: opts.enabled ?? true,
  });
}

export function useStockHistory(opts: { enabled?: boolean; limit?: number } = {}) {
  return useQuery<{ entries: StockHistoryEntry[]; nextBefore: string | null }>({
    queryKey: [...STOCK_KEYS.history, opts.limit ?? 40],
    queryFn: async () => (await axios.get(`${API_URL}/api/stock/history`, { params: { limit: opts.limit ?? 40 } })).data,
    enabled: opts.enabled ?? true,
    refetchInterval: 30000,
  });
}

export async function fetchStockHistoryPage(before: string, limit = 40) {
  return (await axios.get(`${API_URL}/api/stock/history`, { params: { limit, before } })).data as {
    entries: StockHistoryEntry[];
    nextBefore: string | null;
  };
}

/** Po każdej zmianie stanu — przegląd, historia, kolejka kiosku (znaczniki). */
export function useInvalidateStock() {
  const qc = useQueryClient();
  return () => {
    qc.invalidateQueries({ queryKey: STOCK_KEYS.overview });
    qc.invalidateQueries({ queryKey: STOCK_KEYS.history });
    qc.invalidateQueries({ queryKey: ['pack-orders'] });
  };
}

/* ---------- Akcje ---------- */

export async function saveStockConfig(patch: Partial<StockConfig>): Promise<StockConfig> {
  return (await axios.post(`${API_URL}/api/settings/stock`, patch)).data;
}

/** delivery/remove: ile sztuk; adjust: ile JEST na półce (policzone). */
export async function recordStockMovement(input: {
  kind: 'delivery' | 'remove' | 'adjust';
  lines: StockLineInput[];
  reason?: string | null;
}): Promise<{ batchId: string; applied: number; units: number }> {
  return (await axios.post(`${API_URL}/api/stock/movements`, input)).data;
}

export async function undoStockBatch(batchId: string): Promise<{ batchId: string }> {
  return (await axios.post(`${API_URL}/api/stock/batches/${batchId}/undo`)).data;
}

/** Nowa lista „Do dobrania"; `print` = od razu do druku (drukarka raportów). */
export async function createStockRun(print: boolean): Promise<{ run: StockRun | null; jobId: string | null; message: string; printError?: string }> {
  return (await axios.post(`${API_URL}/api/stock/runs`, { print })).data;
}

export async function printStockRun(runId: string): Promise<{ jobId: string; message: string }> {
  return (await axios.post(`${API_URL}/api/stock/runs/${runId}/print`)).data;
}

/** Rozliczenie: ile faktycznie dobrano per pozycja (0 = nie udało się wziąć). */
export async function settleStockRun(runId: string, lines: StockLineInput[]): Promise<{ run: StockRun; units: number }> {
  return (await axios.post(`${API_URL}/api/stock/runs/${runId}/settle`, { lines })).data;
}

export async function cancelStockRun(runId: string): Promise<{ run: StockRun }> {
  return (await axios.post(`${API_URL}/api/stock/runs/${runId}/cancel`)).data;
}

export async function printStockSummary(): Promise<{ jobId: string; message: string }> {
  return (await axios.post(`${API_URL}/api/stock/print/summary`)).data;
}

export async function patchStockItem(
  id: string,
  patch: Partial<Pick<StockCatalogItem, 'displayName' | 'icon' | 'tracked' | 'carryOver' | 'step' | 'safetyStock' | 'sortOrder'>>,
): Promise<{ item: StockCatalogItem }> {
  return (await axios.patch(`${API_URL}/api/stock/items/${id}`, patch)).data;
}

export async function learnStockItems(): Promise<{ added: number; orders: number }> {
  return (await axios.post(`${API_URL}/api/stock/items/learn`)).data;
}

/** PDF w nowej karcie (podgląd / druk z przeglądarki, gdy przy panelu nie ma agenta druku). */
export async function openStockPdf(doc: { kind: 'summary' } | { kind: 'pick'; runId: string }): Promise<void> {
  const url =
    doc.kind === 'summary' ? `${API_URL}/api/stock/print/summary.pdf` : `${API_URL}/api/stock/runs/${doc.runId}/pick.pdf`;
  // Okno otwieramy od razu (w obsłudze kliknięcia), inaczej blokada wyskakujących okien je zatrzyma.
  const win = window.open('', '_blank');
  try {
    const r = await axios.get(url, { responseType: 'blob' });
    const blobUrl = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
    if (win) win.location.href = blobUrl;
    else window.open(blobUrl, '_blank');
    setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
  } catch (e) {
    win?.close();
    throw e;
  }
}

/* ---------- Pomocnicze ---------- */

/** Komunikat błędu z odpowiedzi API (interceptor spłaszcza {error:{code,message}}). */
export function stockErrorText(e: any, fallback = 'Błąd magazynu'): string {
  return e?.response?.data?.error || e?.response?.data?.message || e?.message || fallback;
}

export function stockErrorCode(e: any): string | null {
  return e?.response?.data?.errorCode || null;
}

/** „24:00" dla północy — tak mówią ludzie na zmianie. */
export function resetTimeLabel(resetTime: string): string {
  return resetTime === '00:00' ? '24:00' : resetTime;
}

/** „za 3 h 20 min" do najbliższego resetu. */
export function untilLabel(iso: string | null, now: number = Date.now()): string {
  if (!iso) return '';
  const ms = new Date(iso).getTime() - now;
  if (ms <= 0) return 'za chwilę';
  const min = Math.round(ms / 60000);
  if (min < 60) return `za ${min} min`;
  const h = Math.floor(min / 60);
  const m = min % 60;
  return m ? `za ${h} h ${m} min` : `za ${h} h`;
}

/** Odmiana: plural(n, ['sztuka','sztuki','sztuk']). */
export function plural(n: number, forms: [string, string, string]): string {
  const abs = Math.abs(n);
  if (abs === 1) return forms[0];
  const d = abs % 10;
  const dd = abs % 100;
  return d >= 2 && d <= 4 && (dd < 12 || dd > 14) ? forms[1] : forms[2];
}

export const KIND_META: Record<StockMovementKind, { label: string; icon: string; tone: 'ok' | 'info' | 'warn' | 'err' | 'neutral' }> = {
  delivery: { label: 'Dostawa', icon: 'truck', tone: 'ok' },
  restock: { label: 'Dobranie', icon: 'clipboard-check', tone: 'info' },
  consume: { label: 'Spakowane', icon: 'package-check', tone: 'neutral' },
  remove: { label: 'Zdjęte ze stanu', icon: 'package-minus', tone: 'err' },
  adjust: { label: 'Inwentaryzacja', icon: 'scale', tone: 'warn' },
};
